import type { AutoConnection, RelationshipPair } from '../../entities/graph-document';
import { canonicalRelationshipPairId } from './normalize-explicit-relationship-pair';
import {
  selectRelationshipSummary,
  type RelationshipDisplaySummary,
} from './select-relationship-summary';

export interface ExpandedRelationshipReveal {
  pairId: string;
  sourceNodeId: string;
}

export interface VisibleRelationshipEdge {
  sourceNodeId: string;
  targetNodeId: string;
  derivedConnections: AutoConnection[];
  relationshipPair: RelationshipPair;
  summary: RelationshipDisplaySummary;
}

export function composeVisibleRelationshipEdges({
  connections,
  relationships,
  focusedNodeId = null,
  expandedRelationship = null,
}: {
  connections: AutoConnection[];
  relationships: RelationshipPair[];
  focusedNodeId?: string | null;
  expandedRelationship?: ExpandedRelationshipReveal | null;
}): VisibleRelationshipEdge[] {
  const connectionGroups = groupConnectionsByPairId(connections);

  return relationships
    .map((relationshipPair) => composeVisibleEdge({ connectionGroups, expandedRelationship, focusedNodeId, relationshipPair }))
    .filter((edge): edge is VisibleRelationshipEdge => edge !== null)
    .sort((left, right) => left.relationshipPair.pairId.localeCompare(right.relationshipPair.pairId));
}

function composeVisibleEdge({
  connectionGroups,
  expandedRelationship,
  focusedNodeId,
  relationshipPair,
}: {
  connectionGroups: Map<string, AutoConnection[]>;
  expandedRelationship: ExpandedRelationshipReveal | null;
  focusedNodeId: string | null;
  relationshipPair: RelationshipPair;
}): VisibleRelationshipEdge | null {
  const expandedSourceNodeId = expandedRelationship?.pairId === relationshipPair.pairId
    ? resolveSourceNodeId(relationshipPair, expandedRelationship.sourceNodeId)
    : null;
  const focusedSourceNodeId = focusedNodeId && relationshipPair.endpoints.includes(focusedNodeId)
    ? focusedNodeId
    : null;
  const sourceNodeId = expandedSourceNodeId ?? focusedSourceNodeId;

  if (!sourceNodeId) {
    return null;
  }

  const targetNodeId = relationshipPair.endpoints[0] === sourceNodeId
    ? relationshipPair.endpoints[1]
    : relationshipPair.endpoints[0];
  const derivedConnections = connectionGroups.get(relationshipPair.pairId) ?? [];
  const visibilityMode = expandedSourceNodeId ? 'expand-visible' : 'focus-visible';

  return {
    sourceNodeId,
    targetNodeId,
    derivedConnections,
    relationshipPair,
    summary: selectRelationshipSummary({
      pair: relationshipPair,
      sourceNodeId,
      targetNodeId,
      derivedConnections,
      visibilityMode,
    }),
  };
}

function groupConnectionsByPairId(connections: AutoConnection[]): Map<string, AutoConnection[]> {
  const groups = new Map<string, AutoConnection[]>();

  for (const connection of connections) {
    const pairId = canonicalRelationshipPairId(connection.sourceNodeId, connection.targetNodeId);
    groups.set(pairId, [...(groups.get(pairId) ?? []), connection]);
  }

  return groups;
}

function resolveSourceNodeId(pair: RelationshipPair, sourceNodeId: string): string {
  return pair.endpoints.includes(sourceNodeId) ? sourceNodeId : pair.endpoints[0];
}